import React, { useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import CameraCapture from "@/components/CameraCapture";
import { Camera, Loader2, Trash2, ImageOff } from "lucide-react";

const MAX = 5;
export const photoUrl = (p) => `${API_BASE}/inventory/photos/${p.storage_path}`;

/**
 * Photos on a PO — the pallet, the packing slip, or what actually landed on the dock.
 * Works before the PO exists (pending mode: no poId, photos handed back to the caller)
 * and after (attaches straight to the PO).
 */
export default function POPhotos({ accountId, poId, kind = "inbound", photos = [], onChange, dark = false, compact = false }) {
  const [cam, setCam] = useState(false);
  const [busy, setBusy] = useState(false);
  const mine = photos.filter((p) => (p.kind || "inbound") === kind);
  const full = mine.length >= MAX;

  const upload = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `po-${kind}-${Date.now()}.jpg`);
      fd.append("kind", kind);
      if (poId) {
        const { data } = await api.post(`/inventory/pos/${poId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange?.(data.photos);
      } else {
        fd.append("account_id", accountId);
        const { data } = await api.post("/inventory/po-photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange?.([...photos, data]);
      }
      toast.success("Photo added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      if (poId) {
        const { data } = await api.delete(`/inventory/pos/${poId}/photos/${id}`);
        onChange?.(data.photos);
      } else {
        onChange?.(photos.filter((p) => p.id !== id));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const btnCls = dark
    ? "bg-white/10 hover:bg-white/20 text-white border border-white/15"
    : "bg-white hover:bg-slate-50 text-slate-700 border border-slate-300";

  return (
    <div data-testid={`po-photos-${kind}`}>
      <div className="flex flex-wrap items-center gap-2">
        {!compact && mine.map((p) => (
          <div key={p.id} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shrink-0">
            <a href={photoUrl(p)} target="_blank" rel="noreferrer" data-testid={`po-photo-${p.id}`}>
              <img src={photoUrl(p)} alt={p.original_filename || "PO photo"} className="w-full h-full object-cover" />
            </a>
            <button onClick={() => remove(p.id)} data-testid={`po-photo-remove-${p.id}`}
              className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-red-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button onClick={() => setCam(true)} disabled={busy || full} data-testid={`po-photo-add-${kind}`}
          className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50 transition-colors ${btnCls}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {compact ? (mine.length ? `Photo · ${mine.length}` : "Photo") : full ? `${MAX} photos max` : mine.length ? "Add another photo" : "Add photo"}
        </button>
      </div>
      {!compact && mine.length === 0 && (
        <p className={`text-xs mt-1.5 inline-flex items-center gap-1.5 ${dark ? "text-white/50" : "text-slate-400"}`}>
          <ImageOff className="w-3.5 h-3.5" />
          {kind === "receiving" ? "Snap the boxes or pallet as they come off the truck." : "Snap the pallet or packing slip so receiving knows what's coming."}
        </p>
      )}
      {cam && (
        <CameraCapture
          label={kind === "receiving" ? "Photo of what arrived" : "Photo of the pallet or packing slip"}
          busy={busy}
          onCapture={(blob) => upload(blob)}
          onClose={() => setCam(false)}
        />
      )}
    </div>
  );
}
